import { resolve, singleton } from 'aurelia';
import { HttpClient } from './http-client';

// One row of the image → (brand, toy line) cache as returned by GET /api/admin/image-cache.
// An empty brand represents a cached "unidentified" verdict.
export interface ImageCacheEntry {
  imageKey: string;
  imageUrl: string;
  brand: string;
  toyLine: string;
  createdAt: string;
  // Per-row in-flight guard so a delete disables only its own row while saving.
  deleting?: boolean;
}

// Owns the admin image-cache management panel: lists cache rows and adds/updates/deletes them so
// an admin can hand-tag images when the Brand Vision service is disabled. Mirrors the other
// singleton admin services — shared state lives here and the panel binds to it directly.
@singleton()
export class ImageCacheAdminService {
  private readonly http: HttpClient = resolve(HttpClient);

  panelOpen: boolean = false;
  loading: boolean = false;
  saving: boolean = false;
  error: string | null = null;
  entries: ImageCacheEntry[] = [];

  // "Reprocess unknowns" action state: an in-flight guard and a transient result message shown
  // after reapplying the cache to Unknown-brand/toy-line releases.
  reprocessing: boolean = false;
  reprocessMessage: string | null = null;

  // The current search filter (URL / brand / toy line substring).
  search: string = '';

  // Add/edit form fields. When editImageKey is set, the form is updating an existing row's URL;
  // otherwise it is adding a new one. The URL is the identity, so editing a URL creates a new key.
  formImageUrl: string = '';
  formBrand: string = '';
  formToyLine: string = '';

  // Loads cache rows for the current search filter. Safe to call whenever the panel is opened or
  // the search changes; failures leave a readable error and an empty list.
  async load(): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const query = this.search.trim() ? `?search=${encodeURIComponent(this.search.trim())}` : '';
      const response = await this.http.get(`/api/admin/image-cache${query}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data: ImageCacheEntry[] = await response.json();
      this.entries = data.map((e) => ({ ...e, deleting: false }));
    } catch (e: any) {
      this.error = `Could not load image cache: ${e.message}`;
      this.entries = [];
    } finally {
      this.loading = false;
    }
  }

  async openPanel(): Promise<void> {
    this.panelOpen = true;
    this.reprocessMessage = null;
    this.resetForm();
    await this.load();
  }

  closePanel(): void {
    this.panelOpen = false;
    this.error = null;
  }

  resetForm(): void {
    this.formImageUrl = '';
    this.formBrand = '';
    this.formToyLine = '';
  }

  // Prefills the form from an existing row so the admin can edit and re-save it.
  editEntry(entry: ImageCacheEntry): void {
    this.formImageUrl = entry.imageUrl;
    this.formBrand = entry.brand;
    this.formToyLine = entry.toyLine;
    this.error = null;
  }

  // Adds or updates a cache entry from the form. The row is keyed server-side by the image URL,
  // so submitting an existing URL updates it. Reloads the list on success.
  async save(): Promise<void> {
    if (this.saving) return;
    const imageUrl = this.formImageUrl.trim();
    if (!imageUrl) {
      this.error = 'An image URL is required.';
      return;
    }
    this.saving = true;
    this.error = null;
    try {
      const response = await this.http.sendWithCsrf('POST', '/api/admin/image-cache', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageUrl,
          brand: this.formBrand.trim(),
          toyLine: this.formToyLine.trim()
        })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error ?? `HTTP ${response.status}`);
      }
      this.resetForm();
      await this.load();
    } catch (e: any) {
      this.error = `Could not save entry: ${e.message}`;
    } finally {
      this.saving = false;
    }
  }

  // Deletes a single cache entry by its key, then removes it from the local list on success.
  async deleteEntry(entry: ImageCacheEntry): Promise<void> {
    if (entry.deleting) return;
    entry.deleting = true;
    this.error = null;
    try {
      const response = await this.http.sendWithCsrf(
        'DELETE',
        `/api/admin/image-cache/${encodeURIComponent(entry.imageKey)}`,
        {}
      );
      const ok = response.ok || response.status === 404;
      if (!ok) throw new Error(`HTTP ${response.status}`);
      this.entries = this.entries.filter((e) => e.imageKey !== entry.imageKey);
    } catch (e: any) {
      entry.deleting = false;
      this.error = `Could not delete entry: ${e.message}`;
    }
  }

  // Reapplies the image cache to stored releases still marked "Unknown" (brand or toy line),
  // matching on image URL. This never re-scrapes or calls the vision service; it just fixes up
  // existing rows from hand-tagged or newly-cached images. Shows a transient result count.
  async reprocessUnknowns(): Promise<void> {
    if (this.reprocessing) return;
    this.reprocessing = true;
    this.error = null;
    this.reprocessMessage = null;
    try {
      const response = await this.http.sendWithCsrf('POST', '/api/admin/image-cache/reprocess-unknowns', {});
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error ?? `HTTP ${response.status}`);
      }
      const data = await response.json().catch(() => ({ updated: 0 }));
      const updated = typeof data?.updated === 'number' ? data.updated : 0;
      this.reprocessMessage = updated === 1
        ? 'Updated 1 release from the image cache.'
        : `Updated ${updated} releases from the image cache.`;
    } catch (e: any) {
      this.error = `Could not reprocess unknown releases: ${e.message}`;
    } finally {
      this.reprocessing = false;
    }
  }
}
